import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { attendanceKindFromWorkplaceType, workplaceTypeFromAttendanceKind } from "./attendanceConstants";
import {
  formatAttendanceDateLong,
  formatUtcAsTimeInputValue,
  formatYmdInZone,
  getAttendanceDisplayTimeZone,
  parseDatetimeLocalValueToUtc,
  parseUtcFromApi,
} from "./attendanceDateUtils";
import { Common } from "../../../utils";
import { isInternalBiometricNote } from "./attendanceDailyLogsUtils";
import "./Attendance.css";

const KIND = {
  regular: "regular",
  remote: "remote",
  field: "field",
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {{ id: string, occurredAtUtc: unknown, location?: string, note?: string, projectId?: string, projectIds?: string[], workplaceType?: number } | null} props.event
 * @param {{ id: string, name: string }[]} props.projects
 * @param {boolean} props.loading
 * @param {(payload: Record<string, unknown>) => Promise<void>} props.onSave
 * @param {boolean} [props.submitForApproval] — when true, primary action is “Submit for approval” (correction request) instead of Save.
 */
const AttendanceSelfEditModal = ({ open, onClose, event, projects, loading, onSave, submitForApproval = false }) => {
  const { t } = useTranslation();
  const displayTz = getAttendanceDisplayTimeZone();

  const [kind, setKind] = useState(KIND.regular);
  /** Civil date `YYYY-MM-DD` in display TZ — fixed for this edit (time-only changes). */
  const [occurredDateYmd, setOccurredDateYmd] = useState("");
  const [occurredTime, setOccurredTime] = useState("");
  const [note, setNote] = useState("");
  /** @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]} */
  const [projectIds, setProjectIds] = useState(/** @type {string[]} */ ([]));

  useEffect(() => {
    if (!open || !event) return;
    setKind(attendanceKindFromWorkplaceType(event.workplaceType));
    const d = parseUtcFromApi(event.occurredAtUtc);
    if (d) {
      setOccurredDateYmd(formatYmdInZone(d, displayTz));
      setOccurredTime(formatUtcAsTimeInputValue(event.occurredAtUtc, displayTz));
    } else {
      setOccurredDateYmd("");
      setOccurredTime("");
    }
    setNote(event.note != null && !isInternalBiometricNote(event.note) ? String(event.note) : "");
    const fromList = Array.isArray(event.projectIds)
      ? event.projectIds.map((x) => String(x).trim()).filter(Boolean)
      : [];
    if (fromList.length > 0) {
      setProjectIds(fromList);
    } else if (event.projectId != null && String(event.projectId).trim() !== "") {
      setProjectIds([String(event.projectId).trim()]);
    } else {
      setProjectIds([]);
    }
  }, [open, event, displayTz]);

  const toggleProject = (id) => {
    const key = String(id);
    setProjectIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const handleSubmit = async () => {
    if (!event?.id) return;
    const tm = String(occurredTime).trim();
    const m = tm.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!occurredDateYmd || !m) {
      Common.showErrorMessage(t("app.attendance.dailyLogs.editInvalidTime"));
      return;
    }
    const hh = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    const timePadded = `${String(hh).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    const at = parseDatetimeLocalValueToUtc(`${occurredDateYmd}T${timePadded}`, displayTz);
    if (!at) {
      Common.showErrorMessage(t("app.attendance.dailyLogs.editInvalidTime"));
      return;
    }
    await onSave({
      eventId: event.id,
      occurredAtUtc: at.toISOString(),
      location: event.location != null ? String(event.location) : "",
      note,
      projectIds,
      projectId: projectIds.length > 0 ? projectIds[0] : "",
      workplaceType: workplaceTypeFromAttendanceKind(kind),
    });
  };

  if (!open || !event) return null;

  return (
    <div className="att-modal-overlay" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="att-modal" role="dialog" aria-modal="true" aria-labelledby="att-self-edit-modal-title">
        <div className="att-modal-header">
          <div className="att-modal-title-row">
            <div className="att-modal-icon-wrap" aria-hidden>
              <i className="bi bi-pencil-square" />
            </div>
            <h2 id="att-self-edit-modal-title" className="att-modal-title">
              {t("app.attendance.dailyLogs.editModalTitle")}
            </h2>
          </div>
          <button type="button" className="att-modal-close" onClick={onClose} aria-label={t("app.common.close")}>
            ×
          </button>
        </div>

        <div className="att-modal-body">
          <div className="att-modal-field-white">
            <span className="att-label-upper">{t("app.attendance.dailyLogs.editOccurredDate")}</span>
            <p className="att-edit-occurred-date mb-0">{formatAttendanceDateLong(event.occurredAtUtc, { timeZone: displayTz })}</p>
          </div>
          <div className="att-modal-field-white">
            <span className="att-label-upper">{t("app.attendance.dailyLogs.editOccurredTime")}</span>
            <input
              type="time"
              step={60}
              className="form-control att-edit-time-input"
              value={occurredTime}
              onChange={(e) => setOccurredTime(e.target.value)}
              aria-label={t("app.attendance.dailyLogs.editOccurredTime")}
            />
          </div>

          <div>
            <span className="att-label-upper">{t("app.attendance.modal.attendanceType")}</span>
            <div className="att-segment-toggle">
              <button
                type="button"
                className={kind === KIND.remote ? "att-is-active" : ""}
                onClick={() => setKind(KIND.remote)}
              >
                {t("app.attendance.modal.typeRemote")}
              </button>
              <button
                type="button"
                className={kind === KIND.regular ? "att-is-active" : ""}
                onClick={() => setKind(KIND.regular)}
              >
                {t("app.attendance.modal.typeRegular")}
              </button>
              <button
                type="button"
                className={kind === KIND.field ? "att-is-active" : ""}
                onClick={() => setKind(KIND.field)}
              >
                {t("app.attendance.modal.typeField")}
              </button>
            </div>
          </div>

          <div className="att-modal-field-white">
            <span className="att-label-upper">{t("app.attendance.modal.project")}</span>
            <p className="att-project-multi-hint text-muted small mb-2">
              {t("app.attendance.modal.projectMultiHint")}
            </p>
            {(!projects || projects.length === 0) ? (
              <p className="att-project-multi-empty text-muted small mb-0">
                {t("app.attendance.modal.projectOptional")}
              </p>
            ) : (
              <div className="att-project-checklist" role="group" aria-label={t("app.attendance.modal.project")}>
                {projects.map((p) => {
                  const id = String(p.id);
                  const checked = projectIds.includes(id);
                  return (
                    <label key={id} className={`att-project-check-item${checked ? " is-checked" : ""}`}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={checked}
                        onChange={() => toggleProject(id)}
                      />
                      <span className="att-project-check-label">{p.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="att-modal-field-white">
            <span className="att-label-upper">{t("app.attendance.modal.notes")}</span>
            <textarea
              className="att-textarea att-textarea--white"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("app.attendance.modal.notesPlaceholder")}
            />
          </div>
        </div>

        <div className="att-modal-footer">
          <button type="button" className="att-btn att-btn--ghost" onClick={onClose} disabled={loading}>
            {t("app.common.cancel")}
          </button>
          <button type="button" className="att-btn att-btn--save-primary" onClick={handleSubmit} disabled={loading}>
            {loading
              ? t("app.common.loading")
              : submitForApproval
                ? t("app.attendance.dailyLogs.submitForApproval")
                : t("app.common.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSelfEditModal;
